import type { JSX, ReactNode } from 'react';

import './tabs.css';

export interface AppTabsProps {
  /** Tab elements — typically `<AppTab>` children. */
  children: ReactNode;
  /** Platform variant — "mobile" tightens padding for PWA. */
  variant?: 'desktop' | 'mobile';
  /** Additional class names appended to `.app-tabs`. */
  className?: string;
  /** Optional accessible label for the tab list (screen readers). */
  'aria-label'?: string;
}

/**
 * Per-app second-level tab bar (FR-021, FR-026).
 *
 * Renders a horizontally-scrollable nav below the platform shell. Per-app
 * typography is handled entirely via `tabs.css` descending from the
 * `body.theme-{app}` class set by {@link ThemeBodyClass}. Active state is
 * derived from the URL by the child `<AppTab>` elements (via `NavLink`), so
 * browser back/forward and deep links both work without app-level state.
 */
export function AppTabs({
  children,
  variant = 'desktop',
  className,
  'aria-label': ariaLabel = 'App sections',
}: AppTabsProps): JSX.Element {
  const merged = ['app-tabs', className].filter(Boolean).join(' ');
  return (
    <nav
      className={merged}
      data-variant={variant}
      role="tablist"
      aria-label={ariaLabel}
    >
      {children}
    </nav>
  );
}

export default AppTabs;
